import { Button, Modal } from "@mantine/core";
import { useCallback, useState } from "react";
import type { ApiError } from "../../../../../api";
import { ModalFooter } from "../../../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { getFieldSpecificError } from "../../../../../utils/api-utils";
import type { Meter, Site } from "../../../../../utils/backend-types";
import { getErrorText } from "../../../../../utils/get-error-text";
import type { EnergyDataImportRequest } from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { retryUploadEnergyDataFile } from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { GeneralImportDetailsList } from "../../EnergyDataImportDetailsModal/GeneralImportDetailsList";
import { useDataImportRequestDetails } from "../../hooks/useDataImportRequestDetails";
import { EnergyMeterSelect } from "./EnergyMeterSelect";
import "./MeterIDModal.scss";

interface MeterIDModalProps {
  onClose: () => void;
  dataImportRequest: EnergyDataImportRequest;
  onRetryUploadDone: (dataImportRequest: EnergyDataImportRequest) => void;
  sites: Array<Site>;
  meters: Array<Meter>;
}

function MeterIDModal({
  onClose,
  dataImportRequest,
  onRetryUploadDone,
  sites,
  meters
}: MeterIDModalProps) {
  const { showErrorToast } = useToast();
  const { dataImportRequestWithDetails, isLoading } =
    useDataImportRequestDetails(dataImportRequest.id);

  const [meterID, setMeterID] = useState(0);
  const [submitIsDisabled, setSubmitIsDisabled] = useState(true);

  const handleOnChangeMeter = useCallback((index: number | null) => {
    if (index === null) {
      return;
    }
    setMeterID(index);
    setSubmitIsDisabled(!index);
  }, []);

  const handleRetryUploadRequest = useCallback(() => {
    retryUploadEnergyDataFile(dataImportRequest.id, meterID)
      .then((response) => {
        onRetryUploadDone(response.data);
      })
      .catch((e: ApiError) => {
        const errorMessage = getFieldSpecificError(e, ["selectedMeter"]);
        const errorText = getErrorText(
          e,
          errorMessage ? { [e.response?.status as number]: errorMessage } : {}
        );
        showErrorToast(errorText);
      })
      .finally(() => {
        onClose();
      });
  }, [
    dataImportRequest.id,
    meterID,
    onClose,
    onRetryUploadDone,
    showErrorToast
  ]);

  return (
    <Modal
      className="MeterIDModal"
      opened
      size="lg"
      title={`Importdetails: ${dataImportRequest.energyDataFile.name}`}
      onClose={onClose}
    >
      <GeneralImportDetailsList
        generalDetails={
          isLoading ? dataImportRequest : dataImportRequestWithDetails
        }
      />
      <p>
        Leider konnte in der Datei kein Hinweis auf die Zählernummer (oft die
        Seriennummer) gefunden werden. Bitte wählen Sie den Zähler aus, auf den
        die Daten geladen werden sollen.
      </p>
      <EnergyMeterSelect
        meters={meters}
        sites={sites}
        onChangeMeter={handleOnChangeMeter}
      />
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          color="secondary"
          disabled={submitIsDisabled}
          onClick={() => handleRetryUploadRequest()}
        >
          Wiederholen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { MeterIDModal, MeterIDModalProps };
